import type { Phase, TrialResult, TrialSpec } from '@/domain/core/types';
import { estimateOnsetFromFrame, fitFrames, FrameMonitor } from './Clock';
import type { AssetCache } from './AssetCache';
import type { InputCollector, RawResponse } from './InputCollector';
import type { Renderer } from './Renderer';
import type { TrialSink } from './TrialSink';

export interface RunnerOptions {
  frameMs: number;
  renderer: Renderer;
  assets: AssetCache;
  input: InputCollector;
  sink: TrialSink;
  /** 블록 진행 콜백. 트라이얼 경계에서만 불린다 — React 갱신은 여기서만. */
  onProgress?: (done: number, total: number) => void;
  onFinished?: (results: readonly TrialResult[]) => void;
  onAborted?: (reason: string) => void;
}

/**
 * 트라이얼 실행 루프.
 *
 * 설계 불변 조건:
 *  1. 루프 안에서 객체를 만들지 않는다. 결과 객체는 start() 에서 전부 미리 만든다.
 *  2. 페이즈 지속시간은 프레임으로 센다. 페이즈를 그린 프레임을 0으로 놓고 이후 매 rAF 마다
 *     1씩 올려, 카운트가 frames 에 닿는 프레임(= 그린 프레임 + frames)에서 다음 페이즈를 그린다.
 *     → 표시 지속시간 = 정확히 frames × frameMs. (그린 프레임을 1로 세면 한 프레임 짧아진다 —
 *        E2E 에서 483.3 ms 로 잡힌 실제 버그였다.)
 *  3. 온셋은 그린 프레임의 rAF ts + 1 프레임 (다음 vsync 에 표시되므로).
 *  4. 반응 시각은 event.timeStamp. 핸들러 안의 performance.now() 는 쓰지 않는다.
 *  5. 실측 페이즈 길이 = 다음 페이즈를 그린 프레임 ts − 이 페이즈를 그린 프레임 ts.
 *  6. 탭이 숨겨지면 즉시 중단. 그 트라이얼은 aborted.
 */
export class Runner {
  #o: RunnerOptions;
  #trials: readonly TrialSpec[] = [];
  #results: TrialResult[] = [];
  #monitor: FrameMonitor;
  #raf = 0;
  #running = false;

  // 루프 상태 — 전부 원시값
  #t = 0;                 // 현재 트라이얼
  #p = -1;                // 현재 페이즈 (-1 = 아직 안 들어감)
  #phaseFrames = 0;       // 현재 페이즈에서 센 프레임 수
  #phaseTargetFrames = 0; // 이 페이즈의 목표 프레임 (until-response 면 timeout 프레임)
  #phaseStartTs = 0;
  #phaseDropsAtStart = 0;
  #untilResponse = false;
  #responded = false;
  #lastTs = 0;

  constructor(opts: RunnerOptions) {
    this.#o = opts;
    this.#monitor = new FrameMonitor(opts.frameMs);
  }

  get results(): readonly TrialResult[] { return this.#results; }
  get isRunning(): boolean { return this.#running; }

  start(trials: readonly TrialSpec[]): void {
    if (this.#running) throw new Error('이미 실행 중');
    this.#trials = trials;
    this.#results = trials.map((tr) => ({
      trialIndex: tr.index, blockId: tr.blockId,
      onsetTs: null, responseTs: null, rt: null, response: null, correct: null,
      timedOut: false, inputSource: null, handlerDelayMs: null, prematureInputs: 0,
      phases: tr.phases.map((ph) => ({ id: ph.id, requestedMs: ph.durationMs === 'until-response' ? (ph.timeoutMs ?? 0) : ph.durationMs, frames: 0, actualMs: 0, droppedFrames: 0 })),
      aborted: false,
    }));
    this.#monitor.reset(this.#o.frameMs);
    this.#t = 0; this.#p = -1; this.#phaseFrames = 0;
    this.#running = true;
    document.addEventListener('visibilitychange', this.#onVisibility);
    this.#raf = requestAnimationFrame(this.#tick);
  }

  abort(reason: string): void {
    if (!this.#running) return;
    this.#stopLoop();
    const cur = this.#results[this.#t];
    if (cur) cur.aborted = true;
    Promise.resolve(this.#o.sink.onAbort(this.#trials[0]?.blockId ?? 'unknown', this.#results, reason))
      .then(() => this.#o.onAborted?.(reason))
      .catch(() => this.#o.onAborted?.(reason + ' · 중단 기록 저장 실패'));
  }

  #onVisibility = (): void => {
    if (document.visibilityState === 'hidden') this.abort('visibility-hidden');
  };

  #stopLoop(): void {
    this.#running = false;
    cancelAnimationFrame(this.#raf);
    this.#o.input.disarm();
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  #tick = (ts: number): void => {
    if (!this.#running) return;
    this.#monitor.tick(ts);
    this.#lastTs = ts;

    if (this.#p === -1) {
      this.#enterPhase(0, ts);
    } else {
      this.#phaseFrames += 1;
      const done = this.#untilResponse
        ? (this.#responded || this.#phaseFrames >= this.#phaseTargetFrames)
        : (this.#phaseFrames >= this.#phaseTargetFrames);
      if (done) this.#advance(ts);
    }

    if (this.#running) this.#raf = requestAnimationFrame(this.#tick);
  };

  #enterPhase(idx: number, ts: number): void {
    const trial = this.#trials[this.#t] as TrialSpec;
    const phase = trial.phases[idx] as Phase;
    const result = this.#results[this.#t] as TrialResult;
    const timing = result.phases[idx];

    this.#p = idx;
    this.#phaseFrames = 0;
    this.#phaseStartTs = ts;
    this.#phaseDropsAtStart = this.#monitor.snapshot().droppedFrames;
    this.#untilResponse = phase.durationMs === 'until-response';
    this.#responded = false;

    const fit = fitFrames(this.#untilResponse ? (phase.timeoutMs ?? 2000) : (phase.durationMs as number), this.#monitor.currentFrameMs);
    this.#phaseTargetFrames = fit.frames;
    if (timing) timing.frames = fit.frames;

    this.#o.renderer.execute(phase.ops, this.#o.assets);

    if (phase.rtAnchor) result.onsetTs = estimateOnsetFromFrame(ts, this.#monitor.currentFrameMs);
    if (phase.acceptsInput) this.#o.input.arm(this.#onResponse, this.#onPremature);
    else this.#o.input.disarm(phase.id === 'fixation' || phase.id === 'cue' ? this.#onPremature : null);
  }

  #onResponse = (r: RawResponse): void => {
    const trial = this.#trials[this.#t] as TrialSpec;
    const result = this.#results[this.#t] as TrialResult;
    result.responseTs = r.ts;
    result.response = r.code;
    result.inputSource = r.source;
    result.handlerDelayMs = r.handlerDelayMs;
    result.rt = result.onsetTs === null ? null : r.ts - result.onsetTs;
    result.correct = trial.correctResponse === null ? null : r.code === trial.correctResponse;
    this.#o.renderer.setLastResponse(r.code, result.correct);
    this.#responded = true;
    // 페이즈 전환은 다음 rAF 에서 일어난다 — 여기서 그리지 않는다 (핸들러를 짧게).
  };

  #onPremature = (): void => {
    const result = this.#results[this.#t];
    if (result) result.prematureInputs += 1;
  };

  #advance(ts: number): void {
    const trial = this.#trials[this.#t] as TrialSpec;
    const result = this.#results[this.#t] as TrialResult;
    const timing = result.phases[this.#p];
    if (timing) {
      timing.actualMs = ts - this.#phaseStartTs;
      timing.droppedFrames = this.#monitor.snapshot().droppedFrames - this.#phaseDropsAtStart;
    }
    if (this.#untilResponse && !this.#responded) {
      result.timedOut = true;
      result.correct = trial.correctResponse === null ? null : false;
      this.#o.renderer.setLastResponse(null, false);
    }

    const next = this.#p + 1;
    if (next < trial.phases.length) {
      this.#enterPhase(next, ts);
      return;
    }

    // 트라이얼 종료
    this.#o.onProgress?.(this.#t + 1, this.#trials.length);
    this.#t += 1;
    if (this.#t < this.#trials.length) {
      this.#enterPhase(0, ts);
      return;
    }

    // 블록 종료 — 여기서만 싱크와 React 를 만진다
    this.#stopLoop();
    this.#o.renderer.clear();
    const quality = this.#monitor.snapshot();
    Promise.resolve(this.#o.sink.onBlockEnd(trial.blockId, this.#results, quality))
      .then(() => this.#o.onFinished?.(this.#results))
      .catch(() => this.#o.onAborted?.('세션 결과 저장 실패'));
  }

  get lastFrameTs(): number { return this.#lastTs; }
  quality() { return this.#monitor.snapshot(); }
}
